import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Layout from "../../components/layout";
import {Field, FieldsManager} from "../../data/services/fields.js";
import {resetPassword, resetUserMessage} from "../../data/actions/user";
import LoaderSmall from "../../components/loader-small";
import {Link} from "react-router-dom";
import InfoBar from "../../components/info-paragraph/info-bar";
import {CheckCircleIcon, ChevronLeftIcon, UserIcon} from "@heroicons/react/outline";
import {getProp} from "../../common/util/util-helpers";

const ResetPasswordView = (props) => {

    /** Constants
     ================================================================= */
    const {history, translate} = props

    const dispatch = useDispatch();

    const user = useSelector((state) => state.user)

    /** Fields/Data definitions
     ================================================================= */
    const getFields = () => ({
        username: new Field('username', '', ['empty', 'email'], false, 'email'),
    })

    const [fields, setFields] = useState(getFields())

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        dispatch(resetUserMessage())
    }, [])

    /** Data events
     ================================================================= */
    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSubmit(event);
        }
    };

    const onSubmit = (event) => {
        event && event.preventDefault();

        const validatedFields = FieldsManager.validateFields(fields);

        if (FieldsManager.checkFieldsForErrors(validatedFields)) {
            dispatch(resetPassword(FieldsManager.getFieldKeyValues(validatedFields)));
        } else {
            setFields(validatedFields);
        }
    }

    /** UI events
     ================================================================= */
    const handleInputChange = (name, value) => {
        setFields(FieldsManager.updateField(fields, name, value))
    }

    /** Render
     ================================================================= */
    const inputWrapperClassName =
        "relative flex items-center w-full h-[58px] rounded-[10px] border border-[#dfe3ec] bg-white px-6 transition focus-within:border-[#1f7bea] focus-within:ring-2 focus-within:ring-blue-100";

    const inputIconClassName =
        "mr-5 h-6 w-6 text-[#6f7685] flex-shrink-0";

    const inputClassName =
        "w-full h-full border-none bg-transparent text-[18px] font-medium text-[#1f2937] placeholder:text-[#a7adbb] placeholder:font-medium outline-none";


    return (
        <Layout user={user} history={history}>
            <section className="gradient-form h-[100vh] w-full bg-neutral-200 dark:bg-neutral-700">
                <div className="container h-full m-auto w-full p-10">
                    <div
                        className="g-6 flex h-full flex-wrap items-center justify-center text-neutral-800 dark:text-neutral-200">
                        <div className="w-full xl:w-1/2">
                            <div
                                className="block rounded-lg bg-white shadow-lg dark:bg-neutral-800">
                                <div className="">
                                    <div className="px-4 md:px-0">
                                        <div className="md:mx-6 md:p-12">
                                            <div className="text-center mb-2">
                                                <img
                                                    className="mx-auto w-sm"
                                                    src="/images/nursing_temp_logo.png"
                                                    alt="logo"/>
                                            </div>

                                            <form onKeyPress={onKeyDown}>
                                                <h2 className="mt-4 text-2xl font-bold leading-9 tracking-tight text-center">
                                                    {translate('text.reset_password')}
                                                </h2>

                                                <h2 className=" text-center">
                                                    Enter your email and we&rsquo;ll send you
                                                    instructions to reset your password
                                                </h2>

                                                <div className={inputWrapperClassName + (" pr-0 my-4")}>
                                                    <UserIcon className={inputIconClassName} />
                                                    <input
                                                        className={inputClassName + (" rounded-r-lg")}
                                                        type="text"
                                                        placeholder="Email"
                                                        onChange={(e) => {
                                                            handleInputChange("username", e.target.value)
                                                        }}
                                                        value={getProp(fields, 'username.value', '')}
                                                    />
                                                </div>

                                                {user.isLoading && (
                                                    <div className="flex justify-center">
                                                        <LoaderSmall/>
                                                    </div>
                                                )}

                                                <div className="flex justify-content-end items-center">
                                                    <button
                                                        disabled={user.isLoading}
                                                        className="rounded-lg bg-primary-400 mb-3 inline-block w-full rounded px-6 py-4 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_rgba(0,0,0,0.2)] transition duration-150 ease-in-out hover:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:outline-none focus:ring-0 active:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)]"
                                                        onClick={onSubmit}
                                                    >
                                                        <span className="px-7 relative">
                                                            {!user.reset && (
                                                                translate('btn.reset_password')
                                                            )}

                                                            {user.reset && (
                                                                translate('btn.reset_password_again')
                                                            )}
                                                        </span>
                                                    </button>
                                                </div>
                                            </form>

                                            <div className="min-h-[2.5rem] mt-4">
                                                {user.error && (
                                                    <InfoBar
                                                        type="danger"
                                                        addClass="justify-content-center"
                                                    >
                                                        {translate(user.errorMessage)}
                                                    </InfoBar>
                                                )}

                                                {user.reset && (
                                                    <InfoBar
                                                        type="success"
                                                        Icon={CheckCircleIcon}
                                                        addClass="justify-content-center mb-5">{translate('text.reset_password_sent')}</InfoBar>
                                                )}
                                            </div>

                                            <div className="flex justify-content-end items-center">
                                                <Link className="btn btn-text justify-center w-full font-semibold"
                                                      to={"/login"}
                                                >
                                                    <ChevronLeftIcon className="w-5 h-5 -ml-1 mr-1"/>
                                                    {translate('btn.back_to_login')}
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default ResetPasswordView;
